import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { instantRange, likeContains } from './frontdoor-v3-execution.service';
import { ServerCertificateV3 } from './frontdoor-v3-server-certificate.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** The key types the service generates for a request. */
export type ServerCertificateKeyAlgorithmV3 = 'EC_P256' | 'RSA_2048' | 'RSA_4096';

/** `PENDING` until the signed certificate is uploaded, then `COMPLETED`. It never goes back. */
export type ServerCertificateRequestStatusV3 = 'PENDING' | 'COMPLETED';

/** The key types in the order a form offers them, the service default first. */
export const SERVER_CERTIFICATE_KEY_ALGORITHMS: { value: ServerCertificateKeyAlgorithmV3; label: string }[] = [
    { value: 'EC_P256', label: 'EC P-256' },
    { value: 'RSA_2048', label: 'RSA 2048' },
    { value: 'RSA_4096', label: 'RSA 4096' },
];

/**
 * A server certificate whose key pair the service generated. The customer's CA signs its CSR, and the upload of the
 * signed chain turns it into a server certificate with the same id. The private key is never returned.
 */
export interface ServerCertificateRequestV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    /** The name of the server certificate this request becomes. Reserved while the request is pending. */
    name: string;
    /** The DNS name clients connect to, in lowercase. */
    hostname: string;
    /** The requested DNS SANs, in request order. */
    subjectAlternativeNames: string[];
    /** The subject DN of the CSR, as in `CN=db.example.com,O=Example`. */
    subject: string;
    keyAlgorithm: ServerCertificateKeyAlgorithmV3;
    /** Whether the certificate becomes the account default at upload. */
    isDefault: boolean;
    status: ServerCertificateRequestStatusV3;
    /** The PKCS#10 CSR, PEM encoded. */
    csr: string;
    /** The certificate the request became. Set once it is `COMPLETED`. */
    serverCertificateId?: string | null;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
}

/** A request to generate a key pair and a CSR. */
export interface CreateServerCertificateRequestV3 {
    name: string;
    /** 1 to 100 DNS names. They become the SANs of the CSR, in this order. */
    hostnames: string[];
    /** Optional only when `hostnames` holds one name and it is not a wildcard. */
    hostname?: string;
    commonName?: string;
    organization?: string;
    organizationalUnit?: string;
    /** A two-letter ISO 3166 code. */
    country?: string;
    keyAlgorithm?: ServerCertificateKeyAlgorithmV3;
    isDefault?: boolean;
}

/** The chain the customer's CA issued from the CSR, leaf first. */
export interface CompleteServerCertificateRequestV3 {
    certificate: string;
}

export interface ServerCertificateRequestPageV3 {
    serverCertificateRequests: ServerCertificateRequestV3[];
    total: number;
}

export const DEFAULT_SERVER_CERTIFICATE_REQUEST_PAGE_SIZE = 50;

/** What the listing asks the service to filter by. Each set field narrows the page further. */
export interface ServerCertificateRequestListFilters {
    /** A case-insensitive contains. */
    name?: string;
    statuses?: ServerCertificateRequestStatusV3[];
    keyAlgorithms?: ServerCertificateKeyAlgorithmV3[];
    /** ISO times. The window is closed at `from` and open at `to`. */
    createdFrom?: string;
    createdTo?: string;
}

/** Adds `filters` to `params`. Text goes as a `LIKE` pattern, because the service adds no wildcards of its own. */
export function withServerCertificateRequestFilters(
    params: HttpParams,
    filters: ServerCertificateRequestListFilters = {}
): HttpParams {
    const name = (filters.name ?? '').trim();
    let withFilters = name ? params.set('name', likeContains(name)) : params;
    for (const status of filters.statuses ?? []) {
        withFilters = withFilters.append('status', status);
    }
    for (const keyAlgorithm of filters.keyAlgorithms ?? []) {
        withFilters = withFilters.append('keyAlgorithm', keyAlgorithm);
    }
    const created = instantRange(filters.createdFrom, filters.createdTo);
    return created ? withFilters.set('createdAt', created) : withFilters;
}

/** The label a form or a list shows for a key type. */
export function serverCertificateKeyAlgorithmLabel(keyAlgorithm: string | undefined): string {
    return (
        SERVER_CERTIFICATE_KEY_ALGORITHMS.find((option) => option.value === keyAlgorithm)?.label ?? keyAlgorithm ?? ''
    );
}

/** Reads and writes the server certificate requests of one v3 frontdoor. Every path is scoped by the frontdoor id. */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3ServerCertificateRequestService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** One page of `frontdoorId`'s requests, filtered and sorted by the service. `page` counts from 0. */
    public findServerCertificateRequestPage(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_SERVER_CERTIFICATE_REQUEST_PAGE_SIZE,
        filters: ServerCertificateRequestListFilters = {},
        page = 0
    ): Observable<ServerCertificateRequestPageV3> {
        const params = withServerCertificateRequestFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.http
            .get(this.requestsUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    public getServerCertificateRequest(frontdoorId: string, id: string): Observable<ServerCertificateRequestV3> {
        return this.http
            .get(this.requestUrl(frontdoorId, id), { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateRequestV3));
    }

    /** Generates the key pair and the CSR. The answer carries the CSR. */
    public createServerCertificateRequest(
        frontdoorId: string,
        create: CreateServerCertificateRequestV3
    ): Observable<ServerCertificateRequestV3> {
        return this.http
            .post(this.requestsUrl(frontdoorId), create, { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateRequestV3));
    }

    /** Whether {@link createServerCertificateRequest} would be accepted. Sends the same body with the `nf-validate` header. */
    public validateCreateServerCertificateRequest(
        frontdoorId: string,
        create: CreateServerCertificateRequestV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.requestsUrl(frontdoorId), create);
    }

    /** Uploads the signed chain. The answer is the server certificate the request became, with the same id. */
    public completeServerCertificateRequest(
        frontdoorId: string,
        id: string,
        complete: CompleteServerCertificateRequestV3
    ): Observable<ServerCertificateV3> {
        return this.http
            .put(this.certificateUrl(frontdoorId, id), complete, { headers: this.headers() })
            .pipe(map((response: any) => response as ServerCertificateV3));
    }

    /** Whether {@link completeServerCertificateRequest} would be accepted, checked against the request's CSR. */
    public validateCompleteServerCertificateRequest(
        frontdoorId: string,
        id: string,
        complete: CompleteServerCertificateRequestV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.certificateUrl(frontdoorId, id), complete, 'PUT');
    }

    /** Deletes a pending request and its key. A completed request answers 409. */
    public deleteServerCertificateRequest(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(this.requestUrl(frontdoorId, id), { headers: this.headers() });
    }

    private requestsUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/server-certificate-requests`;
    }

    private requestUrl(frontdoorId: string, id: string): string {
        return `${this.requestsUrl(frontdoorId)}/${id}`;
    }

    private certificateUrl(frontdoorId: string, id: string): string {
        return `${this.requestUrl(frontdoorId, id)}/certificate`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Spring HAL leaves `_embedded` out of an empty page, so its absence is an empty list. */
    private readPage(response: any): ServerCertificateRequestPageV3 {
        const embedded = response?._embedded;
        const collection = embedded?.serverCertificateRequests ?? (embedded ? Object.values(embedded)[0] : undefined);
        if (embedded && !Array.isArray(collection)) {
            this.logger.error('findServerCertificateRequestPage: unrecognised response shape', response);
        }
        const rows = Array.isArray(collection) ? (collection as ServerCertificateRequestV3[]) : [];
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : rows.length;
        return { serverCertificateRequests: rows, total };
    }
}
